import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  TrendingUp, TrendingDown, DollarSign, AlertTriangle, Building2, Loader2, Lock, Shield, Clock,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, AreaChart, Area, CartesianGrid,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUSD = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPct = (n) => (n == null ? "—" : `${Number(n).toFixed(2)}%`);
const tickFmt = (n) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K` : `$${n}`;

/**
 * Public read-only investor report page.
 * URL: /investor/:hash
 * No auth required. Supports optional password gate.
 */
export default function InvestorReport() {
  const { hash } = useParams();
  const [state, setState] = useState({ kind: "loading" });
  const [password, setPassword] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwErr, setPwErr] = useState("");

  const load = async (pw = "") => {
    try {
      const { data } = await axios.get(`${API}/public/investor-reports/${hash}`, {
        headers: pw ? { "X-Investor-Password": pw } : {},
      });
      document.title = `${data.title || "Portfolio Report"} · RealtyVault`;
      setState({ kind: "ok", data, pw });
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail || {};
      if (code === 401 && (detail.password_protected || detail === "Password required")) {
        setState({ kind: "password" });
        return;
      }
      if (code === 410) {
        const d = typeof detail === "object" ? detail : {};
        setState({
          kind: "gone",
          revoked: d.revoked === true,
          expired: d.expired === true,
          message: d.message || (typeof detail === "string" ? detail : "This link is no longer available."),
        });
        return;
      }
      if (code === 404) {
        setState({ kind: "missing" });
        return;
      }
      setState({ kind: "error", message: String(e?.message || e) });
    }
  };

  useEffect(() => { load(""); /* eslint-disable-next-line */ }, [hash]);

  const submitPw = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    setPwErr("");
    try {
      await axios.post(`${API}/public/investor-reports/${hash}/verify`, { password });
      await load(password);
    } catch (e) {
      setPwErr(e?.response?.status === 401 ? "Incorrect password." : "Could not verify. Try again.");
    } finally {
      setPwBusy(false);
    }
  };

  if (state.kind === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500" data-testid="investor-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading investor report…
      </div>
    );
  }

  if (state.kind === "password") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="investor-password-gate">
        <form onSubmit={submitPw} className="bg-white rounded-sm border border-slate-200 p-8 max-w-md w-full shadow-sm">
          <div className="flex items-center gap-2 text-slate-900 mb-2">
            <Lock className="w-5 h-5" />
            <h1 className="text-lg font-bold">Password required</h1>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            This investor report is protected. Enter the password provided to you.
          </p>
          <input
            type="password"
            className="input-clean w-full mb-3"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            data-testid="investor-password-input"
            autoFocus
          />
          {pwErr && <div className="text-sm text-red-600 mb-3" data-testid="investor-password-error">{pwErr}</div>}
          <button
            type="submit"
            disabled={pwBusy || !password}
            className="btn-solid-dark w-full"
            data-testid="investor-password-submit"
          >
            {pwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Unlock report"}
          </button>
        </form>
      </div>
    );
  }

  if (state.kind === "gone") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="investor-gone">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">
            {state.revoked ? "Link revoked" : state.expired ? "Link expired" : "Link unavailable"}
          </h1>
          <p className="text-sm text-slate-500">{state.message}</p>
          <p className="text-xs text-slate-400 mt-4">
            If you need fresh access, ask the sender to issue a new investor-report link.
          </p>
        </div>
      </div>
    );
  }

  if (state.kind === "missing") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-xl font-bold text-slate-900 mb-2">Link not found</h1>
          <p className="text-sm text-slate-500">This investor report link does not exist or has been removed.</p>
        </div>
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 text-sm text-red-600">
        Error loading report: {state.message}
      </div>
    );
  }

  // OK state — render the report
  const { data } = state;
  const p = data.payload || {};
  const h = p.headline || {};
  const mom = p.month_over_month || [];
  const rent = p.rent_collected || {};
  const delta = h.mom_delta;
  const expiresAt = data.expires_at ? new Date(data.expires_at).toLocaleDateString() : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="investor-report-page">
      {/* HERO */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-400/20 rounded-sm px-2 py-1 mb-3">
                <Shield className="w-3 h-3" /> Read-only investor view
              </div>
              {data.owner?.logo_data_uri && (
                <img
                  src={data.owner.logo_data_uri}
                  alt={data.owner?.company || "Company logo"}
                  className="h-12 w-auto object-contain mb-3 bg-white/95 rounded-sm p-1.5 border border-white/20"
                  data-testid="investor-report-logo"
                />
              )}
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }} data-testid="investor-report-title">
                {data.title || "Portfolio Report"}
              </h1>
              {data.owner?.company && (
                <div className="text-sm text-white/70 mt-1" data-testid="investor-report-owner">Prepared by {data.owner.company}</div>
              )}
              {data.note && (
                <p className="text-sm text-white/80 max-w-2xl mt-3 leading-relaxed" data-testid="investor-report-note">
                  {data.note}
                </p>
              )}
            </div>
            {expiresAt && (
              <div className="text-xs font-mono uppercase tracking-widest text-white/50 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Valid through {expiresAt}
              </div>
            )}
          </div>

          {/* HEADLINE KPIS */}
          <div className="flex items-baseline gap-4 flex-wrap">
            <div>
              <div className="overline text-white/60 mb-1">Total portfolio value (income approach)</div>
              <div className="text-3xl sm:text-5xl font-bold font-mono tracking-tight" data-testid="investor-total-value">
                {fmtUSD(h.current_total_value)}
              </div>
            </div>
            {delta != null && (
              <div className={`text-sm font-mono font-bold ${delta >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="investor-mom-delta">
                {delta >= 0 ? "▲" : "▼"} {fmtUSD(Math.abs(delta))}
                <span className="text-xs text-white/50 ml-1">vs last month</span>
              </div>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-3 text-xs">
            <Kpi label="Weighted cap rate" value={fmtPct(h.current_weighted_cap_rate)} />
            <Kpi label="Total NOI" value={fmtUSD(h.current_total_noi)} />
            <Kpi label="Properties" value={h.property_count} />
            <Kpi label="Rent collected — this month" value={fmtUSD(rent.this_month)} sub={rent.month_label} />
            <Kpi label="Rent collected — YTD" value={fmtUSD(rent.ytd)} />
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">

        {/* VALUE TREND */}
        <div className="bg-white border border-slate-200 rounded-sm p-4 mb-6" data-testid="investor-chart-value">
          <div className="overline text-slate-500 mb-2">Portfolio value by month</div>
          {mom.length < 2 ? (
            <EmptyChart label="Value trend unlocks after 2+ months of snapshots." />
          ) : (
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <AreaChart data={mom} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="invValueGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0F172A" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#0F172A" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} />
                  <YAxis tickFormatter={tickFmt} tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} width={60} />
                  <Tooltip formatter={(v) => [fmtUSD(v), "Value"]} contentStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="total_value" stroke="#0F172A" strokeWidth={2} fill="url(#invValueGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* NOI + CAP DUAL */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="investor-chart-noi">
            <div className="overline text-slate-500 mb-2">NOI growth</div>
            {mom.length < 2 ? <EmptyChart label="Needs 2+ months." small /> : (
              <div style={{ width: "100%", height: 160 }}>
                <ResponsiveContainer>
                  <LineChart data={mom} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} />
                    <YAxis tickFormatter={tickFmt} tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} width={50} />
                    <Tooltip formatter={(v) => [fmtUSD(v), "NOI"]} contentStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="total_noi" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="investor-chart-cap">
            <div className="overline text-slate-500 mb-2">Weighted cap rate compression</div>
            {mom.length < 2 ? <EmptyChart label="Needs 2+ months." small /> : (
              <div style={{ width: "100%", height: 160 }}>
                <ResponsiveContainer>
                  <LineChart data={mom} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} />
                    <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} width={40} domain={["auto", "auto"]} />
                    <Tooltip formatter={(v) => [`${v}%`, "Weighted cap"]} contentStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="weighted_cap_rate" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* MOVERS */}
        <div className="grid md:grid-cols-2 gap-4">
          <MoverCard
            testid="investor-top-appreciating"
            title="Top appreciating"
            icon={<TrendingUp className="w-4 h-4 text-emerald-600" />}
            items={p.top_appreciating || []}
            emptyLabel="Need 2+ snapshots to rank movers."
            metric={(it) => (
              <span className="text-emerald-700 font-mono font-bold text-sm">
                {it.delta >= 0 ? "+" : ""}
                {fmtUSD(it.delta)}
                {it.pct_change != null && <span className="text-[10px] text-emerald-500 ml-1">({it.pct_change >= 0 ? "+" : ""}{it.pct_change}%)</span>}
              </span>
            )}
          />
          <MoverCard
            testid="investor-cap-drag"
            title="Cap-rate drag"
            icon={<AlertTriangle className="w-4 h-4 text-amber-600" />}
            items={p.cap_rate_drag || []}
            emptyLabel="No measurable cap-rate drag."
            metric={(it) => (
              <span className="font-mono font-bold text-sm text-amber-700">
                {fmtPct(it.cap_rate)}
                <span className="text-[10px] text-slate-400 ml-1">NOI {fmtUSD(it.noi)}</span>
              </span>
            )}
          />
        </div>

        <div className="mt-8 text-center text-[10px] font-mono uppercase tracking-widest text-slate-400">
          Powered by RealtyVault Manager · Confidential investor view · Do not distribute
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="bg-white/10 rounded-sm px-3 py-2">
      <div className="overline text-white/60">{label}</div>
      <div className="font-mono font-bold text-sm">{value}</div>
      {sub && <div className="text-[10px] text-white/50 mt-0.5">{sub}</div>}
    </div>
  );
}

function EmptyChart({ label, small }) {
  return (
    <div className={`flex items-center justify-center text-center text-slate-400 text-xs font-mono uppercase tracking-widest border border-dashed border-slate-200 rounded-sm ${small ? "py-6" : "py-12"}`}>
      {label}
    </div>
  );
}

function MoverCard({ title, icon, items, metric, emptyLabel, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid={testid}>
      <div className="overline text-slate-500 mb-2 flex items-center gap-2">{icon} {title}</div>
      {items.length === 0 ? (
        <div className="py-6 text-center text-xs text-slate-400">{emptyLabel}</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((it, idx) => (
            <li key={it.property_id || idx} className="py-2 flex items-center gap-3">
              <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate text-sm">{it.property_name || "—"}</div>
                {it.latest_at && (
                  <div className="text-[10px] text-slate-400 font-mono">
                    {(it.first_at || "").slice(0, 10)} → {(it.latest_at || "").slice(0, 10)}
                  </div>
                )}
              </div>
              {metric(it)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
